
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Rocket,
  GraduationCap,
  Trophy,
  ArrowRight,
  Mic,
  Lightbulb
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { UserProfile } from '@/types';

interface QuickAccessFeaturesProps {
  userProfile: UserProfile;
  streakCount: number;
  hasStreakToday: boolean;
}

const QuickAccessFeatures: React.FC<QuickAccessFeaturesProps> = ({
  userProfile,
  streakCount,
  hasStreakToday
}) => {
  return (
    <section className="space-y-4">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Rocket className="h-6 w-6 text-neon-purple" />
        Quick Access
      </h2>
      
      <div className="space-y-4">
        {/* Academy Block */}
        <Card className="neo-card overflow-hidden border border-neon-blue/20 shadow-lg bg-white/5 hover:bg-white/10 transition-all duration-300">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-neon-blue to-transparent"></div>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <CardTitle className="text-lg flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-neon-blue" />
                Artist Academy
              </CardTitle>
              <Badge variant="outline" className="bg-neon-blue/10 text-neon-blue border-neon-blue/30">
                65% Complete
              </Badge>
            </div>
            <CardDescription>Continue your music business course</CardDescription>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="mb-4">
              <h3 className="text-sm font-medium mb-1">Music Business Fundamentals</h3>
              <Progress 
                value={65} 
                className="h-1.5 bg-gray-800"
                indicatorClassName="bg-neon-blue"
              />
            </div>
            <Button variant="outline" size="sm" className="w-full border-neon-blue/30 text-neon-blue hover:bg-neon-blue/10" asChild>
              <Link to="/learning" className="flex items-center justify-center gap-1">
                Continue Learning
                <ArrowRight className="h-3 w-3" />
              </Link>
            </Button>
          </CardContent>
        </Card>
        
        {/* Gamification Block */}
        <Card className="neo-card overflow-hidden border border-neon-purple/20 shadow-lg bg-white/5 hover:bg-white/10 transition-all duration-300">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-neon-purple to-transparent"></div>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="h-5 w-5 text-neon-purple" />
                Artist Journey
              </CardTitle>
              <Badge variant="outline" className="bg-neon-purple/10 text-neon-purple border-neon-purple/30">
                3 Badges
              </Badge>
            </div>
            <CardDescription>Track progress & earn rewards</CardDescription>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="mb-4">
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium">{streakCount} Day Streak</span>
                <span className="text-xs text-gray-400">
                  {hasStreakToday ? "Logged in today!" : "Log in today to continue"}
                </span>
              </div>
              <div className="flex gap-1">
                {[...Array(7)].map((_, i) => (
                  <div 
                    key={i} 
                    className={cn(
                      "h-1.5 flex-1 rounded-full",
                      i < streakCount % 7 ? "bg-neon-purple" : "bg-gray-800"
                    )}
                  />
                ))}
              </div>
            </div>
            <Button variant="outline" size="sm" className="w-full border-neon-purple/30 text-neon-purple hover:bg-neon-purple/10" asChild>
              <Link to="/artist-progress" className="flex items-center justify-center gap-1">
                View Rewards
                <ArrowRight className="h-3 w-3" />
              </Link>
            </Button>
          </CardContent>
        </Card>
        
        {/* Collaboration Block */}
        <Card className="neo-card overflow-hidden border border-neon-pink/20 shadow-lg bg-white/5 hover:bg-white/10 transition-all duration-300">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-neon-pink to-transparent"></div>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mic className="h-5 w-5 text-neon-pink" />
                Collaboration
              </CardTitle>
              <Badge variant="outline" className="bg-neon-pink/10 text-neon-pink border-neon-pink/30">
                2 Active
              </Badge>
            </div>
            <CardDescription>Work with other musicians</CardDescription>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="mb-4 space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 bg-gray-800 rounded"></div>
                  <span className="text-sm font-medium">Midnight Sessions</span>
                </div>
                <span className="text-xs text-gray-400">3 collaborators</span>
              </div>
            </div>
            <Button variant="outline" size="sm" className="w-full border-neon-pink/30 text-neon-pink hover:bg-neon-pink/10" asChild>
              <Link to="/collaboration" className="flex items-center justify-center gap-1">
                Continue Project
                <ArrowRight className="h-3 w-3" />
              </Link>
            </Button>
          </CardContent>
        </Card>
        
        {/* Creative Tools Block */}
        <Card className="neo-card overflow-hidden border border-neon-green/20 shadow-lg bg-white/5 hover:bg-white/10 transition-all duration-300">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-neon-green to-transparent"></div>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-neon-green" />
                Creative Tools
              </CardTitle>
              <Badge variant="outline" className="bg-neon-green/10 text-neon-green border-neon-green/30">
                New AI
              </Badge>
            </div>
            <CardDescription>AI-powered creative assistance</CardDescription>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="mb-4 space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 bg-gray-800 rounded"></div>
                  <span className="text-sm font-medium">Lyric Generator</span>
                </div>
                <span className="text-xs text-gray-400">Try it now</span>
              </div>
            </div>
            <Button variant="outline" size="sm" className="w-full border-neon-green/30 text-neon-green hover:bg-neon-green/10" asChild>
              <Link to="/collaboration?tool=ai" className="flex items-center justify-center gap-1">
                Open AI Tools
                <ArrowRight className="h-3 w-3" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default QuickAccessFeatures;
